import { useRuntimeConfig } from '#imports'
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { tw } from '@backmarket/utils/string/tw'
import { IconBackMarketPromise } from '@ds/icons/IconBackMarketPromise'
import { IconExternalLink } from '@ds/icons/IconExternalLink'
import { IconSwap } from '@ds/icons/IconSwap'

import { CMS } from '~/scopes/cms/routes-names'

import { useGetBmProUtmLinks } from './useGetBmProUtmLinks'
import translations from './useNavLinks.translations'

interface Options {
  withBuyback?: boolean
  variant?: 'small'
}

export interface NavLink {
  name: string
  url: RouteLocationRaw
  label: string
  icon?: object
  classNames?: string
  external?: boolean
}

export function useNavLinks(options: Options = {}) {
  const i18n = useI18n()
  const locale = useI18nLocale()

  const {
    market: { countryCode },

    features,
  } = useMarketplace()
  const { isBuybackButtonEnabled } = useBuybackEnabled()
  const config = useRuntimeConfig()
  const bmProMobileUrl = useGetBmProUtmLinks({ content: 'header_mobile' })

  const links = computed<NavLink[]>(() => {
    const linksConfig = [
      {
        condition: () => features?.repair.enabled,
        name: 'on_demand_repair',
        classNames: tw`md:hidden text-static-brand-hi`,
        url: `https://repair.evy.eu/${locale}/backmarket?utm_source=mobile-nav-menu&utm_medium=website`,
        getLabel: () => i18n(translations.onDemandRepair),
        icon: IconExternalLink,
        external: true,
      },
      {
        condition: () =>
          (options.withBuyback ?? true) && isBuybackButtonEnabled,
        name: 'buyback',
        url: {
          name: CMS.BUYBACK,
          params: { locale, pageName: 'home' },
        },
        getLabel: () => i18n(translations.sellYourTech),
        classNames: tw`text-static-brand-mid`,
        icon: IconSwap,
      },
      {
        condition: () =>
          config.public.FF_ENABLE_ABOUT_US_LINK_HEADER?.includes(countryCode),
        name: 'footer_about_us',
        url: { name: CMS.ABOUT_US, params: { locale } },
        getLabel: () => i18n(translations.aboutUs),
      },
      {
        condition: () =>
          config.public.FF_ENABLE_VERIFIED_REFURBISHED_LINK_HEADER?.includes(
            countryCode,
          ),
        name: 'footer_verified_refurbished',
        url: {
          name: CMS.QUALITY,
          params: { locale },
        },
        getLabel: () => i18n(translations.verifiedRefurbished),
        icon: options.variant === 'small' ? IconBackMarketPromise : undefined,
      },
      {
        condition: () =>
          config.public.FF_ENABLE_BACK_STORIES_LINK_HEADER?.includes(
            countryCode,
          ),
        name: 'footer_back_stories',
        url: {
          name: CMS.BACK_STORIES,
          params: { locale, pageName: 'home' },
        },
        getLabel: () => i18n(translations.backStories),
      },
      {
        condition: () =>
          config.public.FF_ENABLE_REPAIR_AND_CARE_LINK?.includes(countryCode),
        classNames: features?.repair.enabled ? tw`hidden md:block` : undefined,
        name: 'header_to_repair_and_care',
        url: { name: CMS.REPAIR_AND_CARE, params: { locale } },
        getLabel: () => i18n(translations.repairAndCare),
      },
      {
        condition: () =>
          config.public.FF_ENABLE_END_FAST_TECH_LINK?.includes(countryCode),
        name: 'header_to_end_fast_tech',
        url: { name: CMS.END_FAST_TECH, params: { locale } },
        getLabel: () => i18n(translations.endFastTech),
      },
      {
        condition: () =>
          config.public.FF_ENABLE_TECH_JOURNAL_LINK?.includes(countryCode),
        name: 'header_to_tech_journal',
        url: { name: CMS.ARTICLE_ROOT_HUB, params: { locale } },
        getLabel: () => i18n(translations.techJournal),
      },
      {
        condition: () =>
          config.public.FF_ENABLE_BM_PRO_LINK_BY_COUNTRY_MOBILE?.includes(
            countryCode,
          ),
        name: 'header_to_bm_pro_website_mobile',
        classNames: tw`md:hidden`,
        url: bmProMobileUrl,
        getLabel: () => i18n(translations.bmProButton),
      },
      {
        condition: () => true,
        name: 'footer_help',
        classNames: tw`md:hidden`,
        url: `/${locale}/help`,
        getLabel: () => i18n(translations.help),
      },
    ]

    return linksConfig
      .filter((item) => item.condition())
      .map(({ condition, getLabel, ...item }) => ({
        ...item,
        label: getLabel(),
      }))
  })

  return { links }
}
